import {DOCUMENT} from '@angular/common';
import {AfterContentInit, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, Renderer2} from '@angular/core';
import {DcpMedia} from '@app/model/dcp-media';
import {DcpComponentClassifications, JsonService} from '@dcp/configurator';
import {NgbModal, NgbModalOptions} from '@ng-bootstrap/ng-bootstrap';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {CmsComponent, Product} from '@spartacus/core';
import {CmsComponentData, CurrentProductService} from '@spartacus/storefront';
import {Observable, of} from 'rxjs';
import {DcpProductDataService} from '../../data/dcp-product-data.service';
import {DcpProductUtilitiesService} from '../../services/dcp-product-utilities.service';
import {ProductPageDecoratorService} from '../../services/product-page-decorator.service';
import {NavigationLink} from '../navigation-bar/navigation-link';
import {ProductDetailsNavigationLinkService} from '../navigation-bar/product-details-navigation-link.service';
import {ProductDetailsSectionComponent} from '../product-details-section/product-details-section.component';
import {LeadUiType} from '@app/model/template-view.model';

export interface ProductHeroComponent extends CmsComponent {
  classificationsProductData?: DcpComponentClassifications[];
}

@UntilDestroy()
@Component({
  selector: 'dcp-hero',
  templateUrl: './hero.component.html',
  styleUrls: ['./hero.component.scss']
})
export class HeroComponent extends ProductDetailsSectionComponent implements OnInit, OnDestroy, AfterContentInit {

  product$: Observable<Product>;
  product: Product;
  componentData$: Observable<ProductHeroComponent>;
  productClassifications: DcpComponentClassifications[];
  summary: string;
  description: string;
  gallery: DcpMedia[];
  galleryIndex: number;
  navLink: NavigationLink = {
    headline: 'productDetails.atAGlance',
    id: 'hero-section'
  };
  offerUiType$: Observable<LeadUiType>

  constructor(
    @Inject(DOCUMENT) private document: Document,
    public productPageDecoratorService: ProductPageDecoratorService,
    protected productDetailsNavigationLinkService: ProductDetailsNavigationLinkService,
    protected currentProductService: CurrentProductService,
    protected changeDetection: ChangeDetectorRef,
    private cmsData: CmsComponentData<ProductHeroComponent>,
    private modalService: NgbModal,
    private productUtilitiesService: DcpProductUtilitiesService,
    private jsonService: JsonService,
    private renderer: Renderer2,
    private productDataService: DcpProductDataService,
  ) {
    super(currentProductService, changeDetection, productDetailsNavigationLinkService, productUtilitiesService);
    // TODO: replace for real data, when will be ready on configurator/ext - detection which Lead modal should be visible (PrivateOffer or Lead)
    this.offerUiType$ = of(LeadUiType.LF).pipe(untilDestroyed(this))
  }

  get hasContent(): boolean {
    return !!this.summary && !!this.description;
  }

  isVisible(): boolean {
    return !!this.hasContent || (this.gallery?.length > 0);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.componentData$ = this.cmsData.data$;
    this.fetchComponentData();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  ngAfterContentInit(): void {
    super.ngAfterContentInit();
  }

  fetchComponentData() {
    this.componentData$.pipe(untilDestroyed(this)).subscribe((componentData) => {
      if (componentData?.classificationsProductData) {
        this.productClassifications =
          this.jsonService.parseSafely(componentData?.classificationsProductData?.toString()) as DcpComponentClassifications[];
      }
    });
  }

  /**
   * Opens a modal form
   *
   * @param content the modal window
   */
  openModal(content: any) {
    const modalOptions: NgbModalOptions = {centered: true};
    this.modalService.open(content, modalOptions);
  }

  /**
   * Supply all data needed for the template
   */
  prepareData(): void {
    if (this.product) {
      this.addCanonicalLink();
      this.summary = this.product.summary;
      this.description = this.product.description;
      this.prepareGallery();
    }
  }

  /**
   * Add canonical link to <head> of PDP
   * (only for products with target URL)
   */
  addCanonicalLink(): void {
    const pdpTargetUrl = this.productUtilitiesService.getPdpTargetUrl(this.product);
    if (pdpTargetUrl) {
      const link = this.renderer.createElement('link');
      link.setAttribute('rel', 'canonical');
      link.setAttribute('href', pdpTargetUrl);
      this.renderer.appendChild(this.document.head, link);
    }
  }

  /**
   * Data for the media gallery
   */
  prepareGallery() {
    this.gallery = [];
    const productMedias: DcpMedia[] = this.productDataService.getMedia(this.product.data);
    for (const media of productMedias) {
      if (media.type === 'GALLERY') {
        this.gallery.push(media);
      }
    }
    this.galleryIndex = 0;
  }

  showNextGalleryItem() {
    this.galleryIndex = (this.galleryIndex + 1) % this.gallery.length;
  }

  showPreviousGalleryItem() {
    if (this.galleryIndex === 0) {
      this.galleryIndex = this.gallery.length - 1;
      return;
    }
    this.galleryIndex = (this.galleryIndex - 1) % this.gallery.length;
  }
}
